import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import type {
    Reward,
    PointsTransaction,
    Voucher,
    Transaction,
    ReceiptDetails,
} from '~/lib/data-contract/loyalty';
import type { BonusReward } from '../components/BonusPointsList/BonusPointsList';
import type { PointsHistoryItem } from '../components/BonusPointsHistory/BonusPointsHistory';
import type { VouchersCardProps } from '~/shared/components/VouchersCard/VouchersCard';
import type { ReceiptCardData } from '~/lib/data-contract/loyalty';

/**
 * Converts API Reward to UI BonusReward format
 * @param reward - Reward from GetMemberRewards API
 * @returns BonusReward formatted for BonusPointsList component
 */
export const mapRewardToBonusReward = (reward: Reward): BonusReward => {
    const date = new Date(reward.lastvaliddate);
    const expiryDate = format(date, 'd. MMMM', { locale: da });

    return {
        id: reward.couponcode,
        amount: reward.value,
        currency: 'kr',
        expiryDate,
    };
};

/**
 * Converts API PointsTransaction to UI PointsHistoryItem format
 * @param transaction - PointsTransaction from GetPointsList API
 * @returns PointsHistoryItem formatted for BonusPointsHistory component
 */
export const mapTransactionToHistoryItem = (transaction: PointsTransaction): PointsHistoryItem => {
    const date = new Date(transaction.date);
    const formattedDate = format(date, 'd. MMMM yyyy', { locale: da });
    const isPending = Boolean(transaction.pointsPeding);
    const pendingDate = isPending ? format(date, 'dd/MM/yyyy') : undefined;

    return {
        id: transaction.systemId,
        type: mapEntryTypeToHistoryType(transaction.entryType),
        title: getTransactionTitle(transaction),
        date: formattedDate,
        points: transaction.points,
        receipt: transaction.documentNo ? 'loyalty.receipt.viewReceipt' : undefined,
        pointsPending: isPending,
        pendingDate,
    };
};

/**
 * Maps API entry type to UI history type
 * @param entryType - Entry type from API (e.g., "Sales", "Survey")
 * @returns UI-friendly type string
 */
const mapEntryTypeToHistoryType = (entryType: string): string => {
    const typeMap: Record<string, string> = {
        Sales: 'purchase',
        Survey: 'survey',
        Expired: 'expired',
        Converted: 'converted',
        Redeemed: 'redeemed',
    };
    return typeMap[entryType] || entryType.toLowerCase();
};

/**
 * Generates a user-friendly title from transaction data
 * @param transaction - PointsTransaction from API
 * @returns Formatted title string
 */
const getTransactionTitle = (transaction: PointsTransaction): string => {
    // For purchases, show store information
    if (transaction.entryType === 'Sales' && transaction.storeNo) {
        return `Køb - Imerco ${transaction.storeNo}`;
    }

    // For conversions to bonus rewards
    if (transaction.entryType === 'Converted') {
        return 'Bonusreward';
    }

    // Use reason code description if available
    if (transaction.reasoncodeDescription) {
        return transaction.reasoncodeDescription;
    }

    // For surveys
    if (transaction.entryType === 'Survey') {
        return 'Besvaret spørgeskema';
    }

    // For expired points
    if (transaction.entryType === 'Expired') {
        return 'Udløb af point';
    }

    // Fallback to entry type
    return transaction.entryType;
};

/**
 * Extracts points data for the PointsMeterBar component
 * @param accountInfo - API response (may have value array wrapper)
 * @returns Object with current points, target, and reward amount
 */
export const mapAccountInfoToPointsData = (accountInfo?: unknown) => {
    // Handle the actual API response structure which wraps data in a value array
    // e.g. { value: [{ activePoints: 674, ... }] }
    let data: { activePoints?: number; pointConvertLimit?: number } | undefined;

    if (accountInfo && typeof accountInfo === 'object' && 'value' in accountInfo) {
        const valueArray = (accountInfo as { value?: unknown[] }).value;
        if (Array.isArray(valueArray) && valueArray.length > 0) {
            data = valueArray[0] as { activePoints?: number; pointConvertLimit?: number };
        }
    } else if (accountInfo && typeof accountInfo === 'object') {
        data = accountInfo as { activePoints?: number; pointConvertLimit?: number };
    }

    if (!data || typeof data.activePoints === 'undefined') {
        return {
            currentPoints: 0,
            targetPoints: 1500,
            rewardAmount: 25,
        };
    }

    return {
        currentPoints: data.activePoints || 0,
        targetPoints: data.pointConvertLimit || 1500,
        rewardAmount: 25, // This could be dynamic based on rewards data if needed
    };
};

/**
 * Converts API Voucher to UI VouchersCardProps format
 * @param voucher - Voucher from GetVoucherList API
 * @returns VouchersCardProps formatted for VouchersCard component
 */
export const mapVoucherToVouchersCard = (voucher: Voucher): VouchersCardProps => {
    const expiryDate = new Date(voucher.expirationDate);
    const formattedExpiryDate = format(expiryDate, 'd. MMMM yyyy', { locale: da });

    // Map value type to description
    const getDescription = (): string => {
        if (voucher.valueType === 'Discount' && voucher.value) {
            return `Spar ${voucher.value} kr`;
        }
        if (voucher.description) {
            return voucher.description;
        }
        return 'Kan kun bruges én gang';
    };

    return {
        id: voucher.couponcode,
        title: voucher.description || 'Voucher',
        description: getDescription(),
        expiryDate: formattedExpiryDate,
        badgeText: voucher.status === 'Open' ? 'common.active' : undefined,
        imageSrc: voucher.imageCode ? `/assets/vouchers/${voucher.imageCode}.png` : undefined, // Let VouchersCard component handle the fallback
        imageAlt: 'Imerco voucher',
    };
};

/**
 * Extended voucher type with additional details for drawer
 */
export interface VoucherWithDetails extends Omit<VouchersCardProps, 'onRedeem' | 'className'> {
    promoCode?: string;
    barcode?: string;
}

/**
 * Converts API Voucher to extended VoucherWithDetails format
 * @param voucher - Voucher from GetVoucherList API
 * @returns VoucherWithDetails with additional drawer information
 */
export const mapVoucherToVoucherWithDetails = (voucher: Voucher): VoucherWithDetails => {
    const baseVoucher = mapVoucherToVouchersCard(voucher);

    return {
        ...baseVoucher,
        promoCode: voucher.couponcode,
        barcode: voucher.barcode,
    };
};

/**
 * Receipt card type for the receipts list
 */

/**
 * Converts API Transaction (from GetPurchaseHistory) to ReceiptCardData
 * This has much more detailed data than GetPointsList
 * @param purchase - Transaction from GetPurchaseHistory API
 * @returns ReceiptCardData formatted for ReceiptsCard component
 */
export const mapPurchaseToReceiptCard = (purchase: Transaction): ReceiptCardData => {
    const date = purchase.date ? new Date(purchase.date) : new Date();
    // Validate date - if invalid, use current date as fallback
    const validDate = isNaN(date.getTime()) ? new Date() : date;
    const formattedMonth = format(validDate, 'MMMM', { locale: da });

    // Parse amounts - API may return strings or numbers, with comma formatting
    const parseAmount = (value: string | number | undefined): number => {
        if (value == null) return 0;
        if (typeof value === 'number') return value;
        // Remove commas and parse (e.g., "6,309.75" -> 6309.75, "-1,200.00" -> -1200.00)
        const cleaned = String(value).replace(/,/g, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Use absolute value for display (negative amounts are returns)
    const absoluteAmount = Math.abs(parseAmount(purchase.grossAmount));

    return {
        id: purchase.systemId,
        storeName: purchase.storeName || `Imerco ${purchase.storeNo}`,
        date: {
            day: validDate.getDate(),
            month: formattedMonth,
            year: validDate.getFullYear(),
        },
        totalAmount: absoluteAmount.toLocaleString('da-DK', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }),
        productCount: Math.abs(purchase.noOfItemLines),
        pointsEarned: purchase.memberPoints,
    };
};

/**
 * Converts API Transaction (from GetReceipt or GetPurchaseHistory) to ReceiptDetails
 * This includes full receipt data: items, payments, totals, etc.
 * @param transaction - Transaction from GetReceipt or GetPurchaseHistory API
 * @returns ReceiptDetails formatted for ReceiptsCardDrawer component
 */
export const mapReceiptToReceiptDetails = (transaction: Transaction): ReceiptDetails => {
    // Parse amounts - API may return strings or numbers, with comma formatting
    // Define this helper first so it can be used throughout the function
    const parseAmount = (value: string | number | undefined): number => {
        if (value == null) return 0;
        if (typeof value === 'number') return value;
        // Remove commas and parse (e.g., "6,309.75" -> 6309.75, "-1,200.00" -> -1200.00)
        const cleaned = String(value).replace(/,/g, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
    };

    const date = transaction.date ? new Date(transaction.date) : new Date();
    // Validate date - if invalid, use current date as fallback
    const validDate = isNaN(date.getTime()) ? new Date() : date;
    const formattedDate = format(validDate, 'dd-MM-yy', { locale: da });

    // Format time (remove milliseconds if present)
    const time = transaction.time ? transaction.time.split('.')[0] : '';

    // Map receipt items - handle case where getReceiptDetail might be undefined or not an array
    const items = Array.isArray(transaction.getReceiptDetail)
        ? transaction.getReceiptDetail.map((item) => ({
              itemNumber: item.itemNo,
              description: item.itemDescription,
              quantity: item.quantity,
              price: parseAmount(item.price),
              total: parseAmount(item.totalRoundedAmt),
          }))
        : [];

    // Get primary payment method - handle case where getPaymentDetail might be undefined or not an array
    const paymentDetails = Array.isArray(transaction.getPaymentDetail)
        ? transaction.getPaymentDetail
        : [];
    const primaryPayment = paymentDetails[0];
    const paymentMethod = primaryPayment
        ? `${primaryPayment.tenderTypeDescription}${primaryPayment.cardDescription ? ` (${primaryPayment.cardDescription})` : ''}`
        : 'Ikke tilgængelig';

    const grossAmount = parseAmount(transaction.grossAmount);
    // Use API-provided VAT amount if available, otherwise calculate based on VAT percentage or default to 20%
    const vatPercentage = transaction.vATPercentage ?? 20;
    const vat =
        transaction.vATTotalAmount != null
            ? parseAmount(transaction.vATTotalAmount)
            : grossAmount * (vatPercentage / 100);

    // Format store address combining address1 and address2
    const storeAddress = transaction.storeAddress
        ? transaction.storeAddress2
            ? `${transaction.storeAddress}, ${transaction.storeAddress2}`
            : transaction.storeAddress
        : 'Adresse ikke tilgængelig';

    // Format phone number - API provides without country code
    // TODO: decide later what to do with country code
    const storePhone = transaction.storePhoneNo
        ? transaction.storePhoneNo.startsWith('+')
            ? transaction.storePhoneNo
            : `+45 ${transaction.storePhoneNo}`
        : transaction.storeNo
          ? `+45 ${transaction.storeNo}`
          : '';

    // Use API-provided email or fallback to generated one
    const storeEmail =
        transaction.storeEmail || (transaction.storeNo ? `k${transaction.storeNo}@imerco.dk` : '');

    return {
        id: transaction.systemId || '',
        storeName:
            transaction.storeName ||
            (transaction.storeNo ? `Imerco ${transaction.storeNo}` : 'Imerco'),
        storeAddress,
        storePostal: transaction.storePostCode || '',
        storeCity: transaction.storeCity || '',
        storePhone,
        storeEmail,
        storeCVR: '26572517', // Imerco CVR
        barcode: transaction.receiptNo || '',
        barcodeText: transaction.receiptNo || '',
        employee: transaction.staffID || '',
        date: formattedDate,
        time,
        transactionId:
            transaction.transactionNo != null ? transaction.transactionNo.toString() : '',
        items,
        totalAmount: grossAmount,
        paymentMethod,
        vat,
    };
};

/**
 * Converts API Transaction (from GetPurchaseHistory) to ReceiptDetails
 * This is an alias for mapReceiptToReceiptDetails for backward compatibility
 * @param purchase - Transaction from GetPurchaseHistory API
 * @returns ReceiptDetails formatted for ReceiptsCardDrawer component
 * @deprecated Use mapReceiptToReceiptDetails instead
 */
export const mapPurchaseToReceiptDetails = mapReceiptToReceiptDetails;
